import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import apiClient from "@/api/apiClient";
import { ENDPOINTS } from "@/api/endpoints";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  X,
  Save,
  Lock,
  Edit,
  Repeat,
  Infinity as InfinityIcon,
  AlertCircle,
} from "lucide-react";
import { format, addDays, isBefore, isSameDay, startOfDay } from "date-fns";
import { getDoctorName, getRoomName } from "@/utils/dataLookups";

export default function UnavailabilityForm({
  unavailabilityToEdit,
  onSave,
  onClose,
  doctors = [],
}) {
  const [formData, setFormData] = useState({
    resource_type: "doctor",
    provider_name: "",
    doctor_id: "",
    room_id: "",
    equipment_id: "",
    reason: "",
    start_date: "",
    start_time: "",
    end_date: "",
    end_time: "",
    is_all_day: false,
    is_recurring: false,
    recurrence_type: "weekly",
    recurrence_count: 1,
    recurrence_end_date: "",
    is_indefinite: false,
  });

  const [blockedDates, setBlockedDates] = useState({});
  const [selectedDate, setSelectedDate] = useState(null);
  const [restrictedTimes, setRestrictedTimes] = useState([]);
  const [dateRange, setDateRange] = useState({ start: null, end: null });

  const isEditing = !!unavailabilityToEdit;

  // Fetch rooms for room unavailability
  const { data: rooms = [] } = useQuery({
    queryKey: ["rooms"],
    queryFn: () => apiClient.get(ENDPOINTS.SETTINGS.ROOMS),
  });

  // Fetch equipment for equipment unavailability
  const { data: equipment = [] } = useQuery({
    queryKey: ["equipment"],
    queryFn: () => apiClient.get(ENDPOINTS.SETTINGS.EQUIPMENT),
  });

  const activeEquipment = equipment.filter((e) => e.status === "active");

  useEffect(() => {
    if (isEditing) {
      let startDate, endDate, startTime, endTime;

      if (unavailabilityToEdit.date) {
        startDate = unavailabilityToEdit.date;
        endDate = unavailabilityToEdit.date;
        startTime = unavailabilityToEdit.start_time || "";
        endTime = unavailabilityToEdit.end_time || "";
      } else if (
        unavailabilityToEdit.start_time &&
        unavailabilityToEdit.start_time.includes("T")
      ) {
        const startDateTime = new Date(unavailabilityToEdit.start_time);
        const endDateTime = new Date(unavailabilityToEdit.end_time);
        startDate = format(startDateTime, "yyyy-MM-dd");
        endDate = format(endDateTime, "yyyy-MM-dd");
        startTime = unavailabilityToEdit.is_all_day
          ? ""
          : format(startDateTime, "HH:mm");
        endTime = unavailabilityToEdit.is_all_day
          ? ""
          : format(endDateTime, "HH:mm");
      } else {
        startDate = "";
        endDate = "";
        startTime = unavailabilityToEdit.start_time || "";
        endTime = unavailabilityToEdit.end_time || "";
      }

      const editType = unavailabilityToEdit.type || "doctor";

      setFormData({
        id: unavailabilityToEdit.id,
        resource_type: editType,
        provider_name: getDoctorName(
          unavailabilityToEdit.doctor_id,
          doctors
        ),
        doctor_id: unavailabilityToEdit.doctor_id || "",
        room_id: unavailabilityToEdit.room_id || "",
        equipment_id: unavailabilityToEdit.equipment_id || "",
        reason: unavailabilityToEdit.reason || "",
        start_date: startDate,
        start_time: startTime,
        end_date: endDate,
        end_time: endTime,
        is_all_day: unavailabilityToEdit.is_all_day || false,
        is_recurring: unavailabilityToEdit.is_recurring || false,
        recurrence_type: unavailabilityToEdit.recurrence_type || "weekly",
        recurrence_count: unavailabilityToEdit.recurrence_count || 1,
        recurrence_end_date:
          unavailabilityToEdit.recurrence_end_date || "",
        is_indefinite: unavailabilityToEdit.is_indefinite || false,
      });

      if (startDate) {
        setSelectedDate(new Date(startDate));
      }
    } else {
      setFormData({
        resource_type: "doctor",
        provider_name: "",
        doctor_id: "",
        room_id: "",
        equipment_id: "",
        reason: "",
        start_date: "",
        start_time: "",
        end_date: "",
        end_time: "",
        is_all_day: false,
        is_recurring: false,
        recurrence_type: "weekly",
        recurrence_count: 1,
        recurrence_end_date: "",
        is_indefinite: false,
      });
      setSelectedDate(null);
    }
  }, [unavailabilityToEdit, isEditing, doctors]);

  // Generate date range for fetching restrictions (next 60 days)
  useEffect(() => {
    const today = new Date();
    const endDate = addDays(today, 60);
    setDateRange({
      start: format(today, "yyyy-MM-dd"),
      end: format(endDate, "yyyy-MM-dd"),
    });
  }, []);

  // Determine the selected resource ID for fetching restrictions
  const selectedResourceId =
    formData.resource_type === "doctor"
      ? formData.doctor_id
      : formData.resource_type === "room"
        ? formData.room_id
        : formData.equipment_id;

  // Fetch existing bookings for the selected doctor
  const { data: existingBookings = [], isLoading: isBookingsLoading } =
    useQuery({
      queryKey: [
        "bookings",
        "doctor",
        formData.doctor_id,
        dateRange.start,
        dateRange.end,
      ],
      queryFn: async () => {
        if (!dateRange.start || !dateRange.end) return [];
        const dates = [];
        const start = new Date(dateRange.start);
        const end = new Date(dateRange.end);

        for (
          let d = new Date(start);
          d <= end;
          d.setDate(d.getDate() + 1)
        ) {
          dates.push(format(d, "yyyy-MM-dd"));
        }

        const bookingPromises = dates.map((date) =>
          apiClient
            .get(ENDPOINTS.BOOKINGS.BY_DATE(date))
            .then((bookings) => ({ date, bookings }))
            .catch(() => ({ date, bookings: [] }))
        );
        const results = await Promise.all(bookingPromises);
        return results;
      },
      enabled:
        formData.resource_type === "doctor" &&
        !!formData.doctor_id &&
        !!dateRange.start &&
        !!dateRange.end,
      staleTime: 2 * 60 * 1000,
    });

  // Fetch existing unavailability periods
  const {
    data: existingUnavailability = [],
    isLoading: isUnavailabilityLoading,
  } = useQuery({
    queryKey: [
      "unavailability",
      formData.resource_type,
      selectedResourceId,
      dateRange.start,
      dateRange.end,
    ],
    queryFn: () => {
      const params = {
        startDate: dateRange.start,
        endDate: dateRange.end,
      };
      if (formData.resource_type === "doctor") {
        params.doctorId = formData.doctor_id;
        params.type = "doctor";
      } else if (formData.resource_type === "room") {
        params.roomId = formData.room_id;
        params.type = "room";
      } else if (formData.resource_type === "equipment") {
        params.equipmentId = formData.equipment_id;
        params.type = "equipment";
      }
      return apiClient.get(ENDPOINTS.UNAVAILABILITY.LIST, params);
    },
    enabled: !!selectedResourceId && !!dateRange.start && !!dateRange.end,
    staleTime: 2 * 60 * 1000,
  });

  // Process real data to create blocked dates and restrictions
  useEffect(() => {
    if (!selectedResourceId || !dateRange.start || !dateRange.end) {
      setBlockedDates({});
      setRestrictedTimes([]);
      return;
    }

    generateRealRestrictions();
  }, [
    selectedResourceId,
    existingBookings,
    existingUnavailability,
    dateRange,
  ]);

  const generateRealRestrictions = () => {
    const blocked = {};

    // Process existing bookings (only for doctor type)
    if (
      formData.resource_type === "doctor" &&
      existingBookings &&
      existingBookings.length > 0
    ) {
      existingBookings.forEach(({ date, bookings }) => {
        const dayBookings = bookings.filter(
          (booking) =>
            booking.doctorId === formData.doctor_id &&
            booking.status !== "cancelled"
        );

        if (dayBookings.length > 0) {
          const restrictedTimes = dayBookings.map((booking) => ({
            start: booking.startTime,
            end: booking.endTime,
            reason: `Booked: ${booking.serviceName || "Appointment"}`,
          }));

          blocked[date] = {
            fullyBlocked: false,
            reason: "Existing appointments",
            restrictedTimes: restrictedTimes,
          };
        }
      });
    }

    // Process existing unavailability periods
    if (existingUnavailability && existingUnavailability.length > 0) {
      existingUnavailability.forEach((unavail) => {
        const dateKey = unavail.date;

        if (blocked[dateKey]) {
          blocked[dateKey].restrictedTimes.push({
            start: unavail.startTime,
            end: unavail.endTime,
            reason: unavail.reason || "Unavailable",
          });
        } else {
          const isAllDay =
            unavail.startTime === "00:00" && unavail.endTime === "23:59";

          blocked[dateKey] = {
            fullyBlocked: isAllDay,
            reason: unavail.reason || "Unavailable",
            restrictedTimes: isAllDay
              ? []
              : [
                  {
                    start: unavail.startTime,
                    end: unavail.endTime,
                    reason: unavail.reason || "Unavailable",
                  },
                ],
          };
        }
      });
    }

    setBlockedDates(blocked);
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleResourceTypeChange = (type) => {
    setFormData((prev) => ({
      ...prev,
      resource_type: type,
      doctor_id: "",
      room_id: "",
      equipment_id: "",
      provider_name: "",
    }));
    setBlockedDates({});
    setRestrictedTimes([]);
    setSelectedDate(null);
  };

  const handleDateSelect = (date) => {
    if (!date) return;

    const dateKey = format(date, "yyyy-MM-dd");
    const dateRestrictions = blockedDates[dateKey];

    setSelectedDate(date);
    const dateString = format(date, "yyyy-MM-dd");

    setRestrictedTimes(dateRestrictions?.restrictedTimes || []);

    setFormData((prev) => ({
      ...prev,
      start_date: dateString,
      end_date: dateString,
    }));
  };

  const isDateDisabled = (date) => {
    return isBefore(date, startOfDay(new Date()));
  };

  const isDatePartiallyRestricted = (date) => {
    const dateKey = format(date, "yyyy-MM-dd");
    const dateRestrictions = blockedDates[dateKey];
    return (
      dateRestrictions &&
      !dateRestrictions.fullyBlocked &&
      dateRestrictions.restrictedTimes.length > 0
    );
  };

  const isDateAvailable = (date) => {
    const dateKey = format(date, "yyyy-MM-dd");
    const dateRestrictions = blockedDates[dateKey];
    return (
      !dateRestrictions ||
      (!dateRestrictions.fullyBlocked &&
        dateRestrictions.restrictedTimes.length === 0)
    );
  };

  const isTimeRestricted = (time) => {
    if (!time || restrictedTimes.length === 0) return false;

    return restrictedTimes.some((restriction) => {
      const timeMinutes =
        parseInt(time.split(":")[0]) * 60 + parseInt(time.split(":")[1]);
      const startMinutes =
        parseInt(restriction.start.split(":")[0]) * 60 +
        parseInt(restriction.start.split(":")[1]);
      const endMinutes =
        parseInt(restriction.end.split(":")[0]) * 60 +
        parseInt(restriction.end.split(":")[1]);

      return timeMinutes >= startMinutes && timeMinutes < endMinutes;
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const {
      resource_type,
      reason,
      start_date,
      start_time,
      end_date,
      end_time,
      is_all_day,
      is_recurring,
      recurrence_type,
      recurrence_count,
      recurrence_end_date,
      is_indefinite,
    } = formData;

    const baseData = {
      provider_name: formData.provider_name,
      doctor_id: resource_type === "doctor" ? formData.doctor_id : undefined,
      room_id: resource_type === "room" ? formData.room_id : undefined,
      equipment_id:
        resource_type === "equipment" ? formData.equipment_id : undefined,
      type: resource_type,
      date: start_date,
      reason,
      is_all_day,
      is_recurring,
      recurrence_type: is_recurring ? recurrence_type : null,
      recurrence_count: is_recurring
        ? is_indefinite
          ? null
          : recurrence_count
        : null,
      recurrence_end_date: is_recurring
        ? is_indefinite
          ? null
          : recurrence_end_date
        : null,
      is_indefinite: is_recurring ? is_indefinite : false,
    };

    if (isEditing) {
      onSave({
        ...baseData,
        id: formData.id,
        start_time: is_all_day ? "00:00" : start_time,
        end_time: is_all_day ? "23:59" : end_time,
      });
    } else {
      onSave({
        ...baseData,
        start_time: is_all_day ? "00:00" : start_time,
        end_time: is_all_day ? "23:59" : end_time,
      });
    }
  };

  const getSelectedDateRestrictions = () => {
    if (!selectedDate) return null;
    const dateKey = format(selectedDate, "yyyy-MM-dd");
    return blockedDates[dateKey];
  };

  const hasResourceSelected = !!selectedResourceId;
  const isLoading =
    formData.resource_type === "doctor"
      ? isBookingsLoading || isUnavailabilityLoading
      : isUnavailabilityLoading;

  // Get display name for the selected resource
  const getResourceDisplayName = () => {
    if (formData.resource_type === "doctor") {
      return getDoctorName(formData.doctor_id, doctors);
    } else if (formData.resource_type === "room") {
      const room = rooms.find((r) => r.id === formData.room_id);
      return room?.name || "";
    } else {
      const eq = equipment.find((e) => e.id === formData.equipment_id);
      return eq?.name || "";
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 pointer-events-none">
      <Card className="w-full max-w-4xl bg-white/90 backdrop-blur-xl border-slate-200/60 shadow-2xl max-h-[90vh] overflow-y-auto pointer-events-auto">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            {isEditing ? (
              <Edit className="w-5 h-5" />
            ) : (
              <Lock className="w-5 h-5" />
            )}
            {isEditing ? "Edit Blocked Time" : "Block Time Off"}
          </CardTitle>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </CardHeader>
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left Column - Resource and Details */}
              <div className="space-y-6">
                <div className="space-y-4">
                  {/* Resource Type Selector */}
                  <div className="space-y-2">
                    <Label>Resource Type *</Label>
                    <Select
                      value={formData.resource_type}
                      onValueChange={handleResourceTypeChange}
                      disabled={isEditing}
                    >
                      <SelectTrigger data-testid="unavail-resource-type-select">
                        <SelectValue placeholder="Select resource type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem
                          value="doctor"
                          data-testid="unavail-resource-type-doctor"
                        >
                          Doctor
                        </SelectItem>
                        <SelectItem
                          value="room"
                          data-testid="unavail-resource-type-room"
                        >
                          Room
                        </SelectItem>
                        <SelectItem
                          value="equipment"
                          data-testid="unavail-resource-type-equipment"
                        >
                          Equipment
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Doctor Selector */}
                  {formData.resource_type === "doctor" && (
                    <div className="space-y-2">
                      <Label>Provider Name *</Label>
                      <Select
                        value={formData.doctor_id}
                        onValueChange={(value) => {
                          setFormData((prev) => ({
                            ...prev,
                            doctor_id: value,
                            provider_name: getDoctorName(value, doctors),
                          }));
                        }}
                        required
                        disabled={isEditing}
                      >
                        <SelectTrigger data-testid="unavail-doctor-select">
                          <SelectValue placeholder="Select a provider" />
                        </SelectTrigger>
                        <SelectContent>
                          {doctors.map((doctor, index) => (
                            <SelectItem
                              key={doctor.id}
                              value={doctor.id}
                              data-testid={`unavail-doctor-option-${index}`}
                            >
                              {getDoctorName(doctor.id, doctors)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Room Selector */}
                  {formData.resource_type === "room" && (
                    <div className="space-y-2">
                      <Label>Room *</Label>
                      <Select
                        value={formData.room_id}
                        onValueChange={(value) => {
                          const room = rooms.find((r) => r.id === value);
                          setFormData((prev) => ({
                            ...prev,
                            room_id: value,
                            provider_name: room?.name || "",
                          }));
                        }}
                        required
                        disabled={isEditing}
                      >
                        <SelectTrigger data-testid="unavail-room-select">
                          <SelectValue placeholder="Select a room" />
                        </SelectTrigger>
                        <SelectContent>
                          {rooms
                            .filter((r) => r.status === "active")
                            .map((room, index) => (
                              <SelectItem
                                key={room.id}
                                value={room.id}
                                data-testid={`unavail-room-option-${index}`}
                              >
                                {room.name}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Equipment Selector */}
                  {formData.resource_type === "equipment" && (
                    <div className="space-y-2">
                      <Label>Equipment *</Label>
                      <Select
                        value={formData.equipment_id}
                        onValueChange={(value) => {
                          const eq = equipment.find((e) => e.id === value);
                          setFormData((prev) => ({
                            ...prev,
                            equipment_id: value,
                            provider_name: eq?.name || "",
                          }));
                        }}
                        required
                        disabled={isEditing}
                      >
                        <SelectTrigger data-testid="unavail-equipment-select">
                          <SelectValue placeholder="Select equipment" />
                        </SelectTrigger>
                        <SelectContent>
                          {activeEquipment.map((eq, index) => (
                            <SelectItem
                              key={eq.id}
                              value={eq.id}
                              data-testid={`unavail-equipment-option-${index}`}
                            >
                              {eq.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="reason">Reason (Optional)</Label>
                    <Input
                      id="reason"
                      value={formData.reason}
                      onChange={(e) =>
                        handleInputChange("reason", e.target.value)
                      }
                      placeholder="e.g., Conference, Maintenance, Calibration"
                      data-testid="unavail-reason-input"
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="is_all_day"
                    checked={formData.is_all_day}
                    onCheckedChange={(checked) =>
                      handleInputChange("is_all_day", checked)
                    }
                    data-testid="unavail-all-day-checkbox"
                  />
                  <Label htmlFor="is_all_day">All Day Event</Label>
                </div>

                {!formData.is_all_day && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="start_time">Start Time *</Label>
                      <Input
                        id="start_time"
                        type="time"
                        value={formData.start_time}
                        onChange={(e) =>
                          handleInputChange("start_time", e.target.value)
                        }
                        required={!formData.is_all_day}
                        data-testid="unavail-start-time-input"
                        className={
                          isTimeRestricted(formData.start_time)
                            ? "border-red-300 bg-red-50"
                            : ""
                        }
                      />
                      {isTimeRestricted(formData.start_time) && (
                        <p className="text-xs text-red-600 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          This time conflicts with existing appointments
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="end_time">End Time *</Label>
                      <Input
                        id="end_time"
                        type="time"
                        value={formData.end_time}
                        onChange={(e) =>
                          handleInputChange("end_time", e.target.value)
                        }
                        required={!formData.is_all_day}
                        data-testid="unavail-end-time-input"
                        className={
                          isTimeRestricted(formData.end_time)
                            ? "border-red-300 bg-red-50"
                            : ""
                        }
                      />
                    </div>
                  </div>
                )}

                {/* Recurring Options */}
                {!isEditing && (
                  <div className="space-y-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="is_recurring"
                        checked={formData.is_recurring}
                        onCheckedChange={(checked) =>
                          handleInputChange("is_recurring", checked)
                        }
                      />
                      <Label
                        htmlFor="is_recurring"
                        className="flex items-center gap-2"
                      >
                        <Repeat className="w-4 h-4" />
                        Make this recurring
                      </Label>
                    </div>

                    {formData.is_recurring && (
                      <div className="pl-6 space-y-4 border-l-2 border-blue-200">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="recurrence_type">Repeat</Label>
                            <Select
                              value={formData.recurrence_type}
                              onValueChange={(value) =>
                                handleInputChange("recurrence_type", value)
                              }
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="daily">Daily</SelectItem>
                                <SelectItem value="weekly">Weekly</SelectItem>
                                <SelectItem value="monthly">
                                  Monthly
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          {!formData.is_indefinite && (
                            <div className="space-y-2">
                              <Label htmlFor="recurrence_count">
                                Number of Occurrences
                              </Label>
                              <Input
                                id="recurrence_count"
                                type="number"
                                min="1"
                                max="365"
                                value={formData.recurrence_count}
                                onChange={(e) =>
                                  handleInputChange(
                                    "recurrence_count",
                                    parseInt(e.target.value)
                                  )
                                }
                              />
                            </div>
                          )}
                        </div>

                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="is_indefinite"
                            checked={formData.is_indefinite}
                            onCheckedChange={(checked) =>
                              handleInputChange("is_indefinite", checked)
                            }
                          />
                          <Label
                            htmlFor="is_indefinite"
                            className="flex items-center gap-2"
                          >
                            <InfinityIcon className="w-4 h-4" />
                            Repeat indefinitely
                          </Label>
                        </div>

                        {!formData.is_indefinite && (
                          <div className="space-y-2">
                            <Label htmlFor="recurrence_end_date">
                              End Recurrence By (Optional)
                            </Label>
                            <Input
                              id="recurrence_end_date"
                              type="date"
                              value={formData.recurrence_end_date}
                              onChange={(e) =>
                                handleInputChange(
                                  "recurrence_end_date",
                                  e.target.value
                                )
                              }
                              min={formData.start_date}
                            />
                            <p className="text-xs text-slate-500">
                              If specified, recurring events will stop after
                              this date, regardless of the number of
                              occurrences.
                            </p>
                          </div>
                        )}

                        {formData.is_indefinite && (
                          <div className="bg-blue-50 p-3 rounded-lg">
                            <p className="text-sm text-blue-700">
                              <InfinityIcon className="w-4 h-4 inline mr-1" />
                              This will create recurring blocked time that
                              continues indefinitely. You can edit or delete
                              individual occurrences later if needed.
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Selected date info */}
                {selectedDate && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="text-sm font-medium text-green-900">
                      Selected Date
                    </div>
                    <div className="text-green-700">
                      {format(selectedDate, "EEEE, MMM d, yyyy")}
                    </div>
                    <div className="text-xs text-green-600 mt-1">
                      Form date: {formData.start_date}
                    </div>
                  </div>
                )}
              </div>

              {/* Right Column - Calendar */}
              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-medium">
                    Select Date *
                  </Label>
                  {!hasResourceSelected && (
                    <p className="text-xs text-slate-500 mb-2">
                      Please select a{" "}
                      {formData.resource_type === "doctor"
                        ? "provider"
                        : formData.resource_type}{" "}
                      first
                    </p>
                  )}
                  {hasResourceSelected && isLoading && (
                    <p className="text-xs text-blue-600 mb-2 flex items-center gap-1">
                      <div className="w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                      Loading availability...
                    </p>
                  )}
                  <div className="mt-2 flex justify-center">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={handleDateSelect}
                      disabled={(date) => {
                        return (
                          !hasResourceSelected ||
                          isDateDisabled(date) ||
                          isLoading
                        );
                      }}
                      className="rounded-md border"
                      modifiers={{
                        available: (date) =>
                          isDateAvailable(date) &&
                          !isBefore(date, startOfDay(new Date())),
                        restricted: (date) =>
                          isDatePartiallyRestricted(date),
                        blocked: (date) => {
                          const dateKey = format(date, "yyyy-MM-dd");
                          return blockedDates[dateKey]?.fullyBlocked;
                        },
                        selected: (date) =>
                          selectedDate && isSameDay(date, selectedDate),
                      }}
                      modifiersStyles={{
                        available: {
                          backgroundColor: "#dbeafe",
                          color: "#1d4ed8",
                        },
                        restricted: {
                          backgroundColor: "#fef3c7",
                          color: "#d97706",
                        },
                        blocked: {
                          backgroundColor: "#fecaca",
                          color: "#dc2626",
                          textDecoration: "line-through",
                        },
                        selected: {
                          border: "2px solid #16a34a",
                          backgroundColor: "#dcfce7",
                          color: "#166534",
                          fontWeight: "bold",
                        },
                      }}
                    />
                  </div>
                </div>

                {/* Legend */}
                <div className="flex flex-wrap justify-center gap-4 text-xs">
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 bg-blue-200 rounded"></div>
                    <span>Available</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 bg-yellow-200 rounded"></div>
                    <span>Partially Available</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 bg-red-200 rounded"></div>
                    <span>Unavailable</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 bg-green-200 border-2 border-green-600 rounded"></div>
                    <span>Selected</span>
                  </div>
                </div>

                {/* Show restrictions for selected date */}
                {selectedDate && getSelectedDateRestrictions() && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertCircle className="w-4 h-4 text-yellow-600" />
                      <span className="text-sm font-medium text-yellow-800">
                        Restricted Times on{" "}
                        {format(selectedDate, "MMM d, yyyy")}
                      </span>
                    </div>
                    <div className="space-y-1">
                      {getSelectedDateRestrictions().restrictedTimes.map(
                        (restriction, index) => (
                          <div
                            key={index}
                            className="text-xs text-yellow-700"
                          >
                            {restriction.start} - {restriction.end}:{" "}
                            {restriction.reason}
                          </div>
                        )
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-6 border-t">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-gradient-to-r from-blue-600 to-indigo-600"
                data-testid="unavail-submit-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {isEditing ? "Update" : "Block"} Time
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
